import { put, takeLatest, call, select } from 'redux-saga/effects';
import { IAsyncAction, IAppState } from '../types';
import { storeBusinesses, storeSelectedBusiness } from './actions';
import {
  GET_BUSINESS_BOOKINGS,
  GET_BUSINESSES,
  CREATE_BOOKING,
  GET_BUSINESS_BY_ID
} from './constants';
import { IGetBusinessBookings, ICreateBookingAction, IGetBusinessById } from './types';
import axios from 'axios';
import moment from 'moment-timezone';
import { parseFullName } from '../../utils/helpers';

const PLATFORM_BASE_URL = `${process.env.REACT_APP_PLATFORM_API_BASE_URL}`

function* getBusinesses(action: IAsyncAction, retry: number = 2): any {
  try {

    const API_URL = PLATFORM_BASE_URL;
    console.log('API_URL', API_URL);

    let url = `${API_URL}/businesses`;
    const axios = require('axios').default;
    let resp: any = null;

    yield axios
      .get(url)
      .then(function (response: any) {
        if (response) {
          console.log('✓ Businesses fetched successfully:', response.data?.length || 0, 'businesses');
          resp = response;
          if (action.callback) {
            action.callback(response, undefined);
          }
        } else {
          console.error('✗ Empty response from businesses API');
          if (action.callback) {
            action.callback(undefined, response);
          }
        }
      })
      .catch(function (error: any) {
        console.error('✗ Failed to fetch businesses:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: url,
          data: error.response?.data
        });
        if (error) {
          if (action.callback) {
            action.callback(undefined, error);
          }
        }
      });
    if (resp) {
      yield put(storeBusinesses(resp.data));
    } else {
      console.warn('⚠ No businesses data to store in Redux');
    }
  } catch (e: any) {
    console.error('✗ Exception in getBusinesses saga:', {
      error: e,
      message: e.message,
      stack: e.stack
    });
    if (e === 'Network Error' && retry > 0) {
      console.log('Retrying... attempts remaining:', retry);
    } else {
      const attributes = {
        type: 'SAGAS ERROR CATCHED',
        action,
        location: 'state/api/sagas - getBusinesses',
      };
      if (action.callback) {
        action.callback(undefined, attributes);
      }
    }
  }
}

function* getBusinessBookings(action: IGetBusinessBookings, retry: number = 2): any {
  try {
    const API_URL = PLATFORM_BASE_URL;
    console.log('API_URL', API_URL);

    const authUser = yield select((state: IAppState) => state.auth.authUser);

    if (!authUser || !authUser.userId) {
      console.warn('⚠️ Cannot fetch userBookings: No authenticated user or userId');
      if (action.callback) {
        action.callback(undefined, 'No authenticated user');
      }
      return;
    }

    let url = `${API_URL}/userBookings/${authUser.userId}/${action.businessId}`;
    console.log('url', url);

    const axios = require('axios').default;
    let resp: any = null;

    yield axios
      .get(url)
      .then(function (response: any) {
        if (response) {
          resp = response;
          if (action.callback) {
            action.callback(response, undefined);
          }
        } else {
          if (action.callback) {
            action.callback(undefined, response);
          }
        }
      })
      .catch(function (error: any) {
        if (error) {
          if (action.callback) {
            action.callback(undefined, error);
          }
        }
      });
    // if (resp) {
    //   yield put(storeBusinesses(resp.data));
    // }
  } catch (e) {
    if (e === 'Network Error' && retry > 0) {
    } else {
      const attributes = {
        type: 'SAGAS ERROR CATCHED',
        action,
        location: 'state/api/sagas - getDevices',
      };
      if (action.callback) {
        action.callback(undefined, attributes);
      }
    }
  }
}

function* createBookingSaga(action: ICreateBookingAction): Generator<any, void, any> {
  try {
    const { payload, callback } = action;
    const API_URL = `${PLATFORM_BASE_URL}/addBooking`;

    console.log('🚀 [SAGA] createBookingSaga started');
    console.log('🚀 [SAGA] API URL:', API_URL);

    // Parse fullName into firstName/lastName for backend
    const { firstName, lastName } = parseFullName(payload.userInfo.fullName || '');

    // Data transformation from frontend to backend format
    const normalizedEmail = payload.userInfo.email.toLowerCase().trim();
    const businessTz = payload.businessTimezone || 'America/Montevideo';
    const dateStr = moment(payload.selectedDatePicker).format('YYYY-MM-DD');
    const apiPayload = {
      businessId: payload.businessId,
      bookDate: moment.tz(dateStr, 'YYYY-MM-DD', businessTz).unix(),
      employeeId: payload.selectedEmployee?.id || null,
      userEmail: normalizedEmail,
      serviceIds: payload.selectedServices?.map((s: any) => s.id) || [], // FIX: was "services"
      selectedTime: payload.selectedTimeSlot,
      paymentMethodId: payload.paymentMethodId || "ON_LOCATION",
      paymentMethod: payload.paymentMethod,
      clientInfo: {  // NEW - for guest checkout client creation
          firstName,
          lastName,
          email: normalizedEmail,
          phone: payload.userInfo.phone
      }
    };

    console.log('📤 [SAGA] Sending request to backend with payload:', apiPayload);
    console.log('📤 [SAGA] Payment details:', {
      paymentMethodId: apiPayload.paymentMethodId,
      paymentMethod: apiPayload.paymentMethod,
      willCreatePaymentPreference: apiPayload.paymentMethod === 'CREDIT_CARD'
    });

    // Proper Redux-Saga pattern using call effect
    const response = yield call(axios.post, API_URL, apiPayload);

    console.log('✅ [SAGA] Booking created successfully!');
    console.log('📥 [SAGA] Response status:', response.status);
    console.log('📥 [SAGA] Response data:', response.data);
    console.log('📥 [SAGA] Response structure:', {
      hasBooking: !!response.data?.booking,
      hasPayment: !!response.data?.payment,
      hasInitPoint: !!response.data?.payment?.initPoint,
      bookingId: response.data?.booking?.id || response.data?.id
    });

    // Success callback
    if (callback) {
      console.log('✅ [SAGA] Calling success callback with response data');
      callback(response.data, undefined);
    }

  } catch (error: any) {
    console.error('❌ [SAGA] Create Booking Error:', error);
    console.error('❌ [SAGA] Error response:', error.response);
    console.error('❌ [SAGA] Error status:', error.response?.status);
    console.error('❌ [SAGA] Error data:', error.response?.data);
    
    // Failure callback with detailed error information
    if (action.callback) {
      const errorData = error.response?.data || error.message || 'Unknown error occurred';
      action.callback(undefined, {
        message: errorData,
        status: error.response?.status,
        originalError: error
      });
    }
  }
}


function* getBusinessByIdSaga(action: IGetBusinessById): Generator<any, void, any> {
  try {
    const API_URL = PLATFORM_BASE_URL;
    const url = `${API_URL}/getBusinessById/${action.businessId}`;

    console.log('🔍 [SAGA] getBusinessById - Fetching business with bookings:', url);

    const response = yield call(axios.get, url);

    console.log('✅ [SAGA] getBusinessById - Response received');
    console.log('📦 [SAGA] Business data received, bookings count:', response.data?.bookings?.length || 0);

    // Extract business and bookings from response
    const { bookings, ...business } = response.data;

    // Store in Redux
    yield put(storeSelectedBusiness(business, bookings || []));

    if (action.callback) {
      action.callback(response.data, undefined);
    }
  } catch (error: any) {
    console.error('❌ [SAGA] getBusinessById Error:', error);
    if (action.callback) {
      action.callback(undefined, error);
    }
  }
}

export default function* apiSagas() {
  yield takeLatest(GET_BUSINESSES, getBusinesses);
  yield takeLatest(GET_BUSINESS_BOOKINGS, getBusinessBookings);
  yield takeLatest(CREATE_BOOKING, createBookingSaga);
  yield takeLatest(GET_BUSINESS_BY_ID, getBusinessByIdSaga);
}
